"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import classNames from "classnames";
import { forwardRef, ReactNode } from "react";

export type TooltipPosition = "top" | "right" | "bottom" | "left";
export type TooltipAlign = "start" | "center" | "end";

export interface TooltipProps {
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** The content to display in the tooltip */
  content: ReactNode;
  /** Side of the trigger to show the tooltip */
  side?: TooltipPosition;
  /** Alignment relative to the trigger */
  align?: TooltipAlign;
  /** Use dark/inverted styling */
  inverted?: boolean;
  /** Allow wider content (max-width: 350px instead of 250px) */
  wide?: boolean;
  /** Custom className for the content container */
  contentClassName?: string;
  /** Delay in ms before showing (default: 200) */
  delayDuration?: number;
  /** Whether tooltip can be triggered by hover and/or click */
  triggerMode?: "hover" | "click" | "both";
  /** Offset from the trigger element in pixels */
  sideOffset?: number;
  /** Whether the tooltip is open (controlled mode) */
  open?: boolean;
  /** Callback when open state changes (controlled mode) */
  onOpenChange?: (open: boolean) => void;
  /** Disable the tooltip */
  disabled?: boolean;
}

/**
 * Tooltip component built on Radix UI
 *
 * @example
 * // Basic usage with icon trigger
 * <Tooltip content="Help text here">
 *   <Icon type="info" className="w-5 h-5" />
 * </Tooltip>
 *
 * @example
 * // With custom positioning and wide content
 * <Tooltip
 *   content={<div className="prose">Rich content</div>}
 *   side="top"
 *   align="end"
 *   wide
 * >
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Inverted (dark) style
 * <Tooltip content="Dark tooltip" inverted>
 *   <span>Trigger</span>
 * </Tooltip>
 */
const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      children,
      content,
      side = "top",
      align = "center",
      inverted = false,
      wide = false,
      contentClassName,
      delayDuration = 200,
      triggerMode = "hover",
      sideOffset = 5,
      open,
      onOpenChange,
      disabled = false,
    },
    ref
  ) => {
    if (disabled || !content) {
      return <>{children}</>;
    }

    // For click mode, we need to handle it differently
    const disableHoverableContent = triggerMode === "click";

    return (
      <TooltipPrimitive.Provider delayDuration={delayDuration}>
        <TooltipPrimitive.Root
          open={open}
          onOpenChange={onOpenChange}
          disableHoverableContent={disableHoverableContent}
        >
          <TooltipPrimitive.Trigger asChild ref={ref}>
            {/* Wrap in span to ensure we have a valid trigger element */}
            <span className="inline-flex cursor-pointer">{children}</span>
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              side={side}
              align={align}
              sideOffset={sideOffset}
              className={classNames(
                // Base styles
                "z-50 rounded-lg shadow-lg",
                "animate-in fade-in-0 zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                "data-[side=bottom]:slide-in-from-top-2",
                "data-[side=left]:slide-in-from-right-2",
                "data-[side=right]:slide-in-from-left-2",
                "data-[side=top]:slide-in-from-bottom-2",
                // Width
                wide ? "max-w-[350px]" : "max-w-[250px]",
                // Color variants
                inverted
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-900 border border-gray-200",
                contentClassName
              )}
            >
              {content}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }
);

Tooltip.displayName = "Tooltip";

export default Tooltip;
